/* global React */
/* ──────────────────────────────────────────────────────────────────────
   Navbar — top navigation bar, shared across every page.
   Reads its data from window.DZ_GLOBAL.navbar (content/global.js).

   Supports two link shapes:
     1. Flat link    → { label, href }                         → renders as <a>
     2. Dropdown     → { label, children: [{label, href}…] }   → renders as
                       a hover-revealed glass-blur submenu beneath the parent
                       button. Children are flat links (label only).

   Dropdown close behaviour (JS-controlled, not CSS-only) so the menu
   doesn't get stuck:
     ▸ Hover the dropdown wrapper → opens
     ▸ Mouse leaves the wrapper   → closes (after 80ms grace period for
                                    the cursor to cross the bridge gap)
     ▸ Click the parent button    → toggles
     ▸ Click outside the dropdown → closes
     ▸ Press Escape               → closes
   ────────────────────────────────────────────────────────────────────── */

const { useState: useNavState, useEffect: useNavEffect, useRef: useNavRef } = React;

/* Mobile menu — "Roadmap" design. Each page is a node on a vertical
   spine; the active page (matched against window.location.pathname)
   pulses; Solutions opens a sub-rail with smaller hollow child nodes;
   "Talk to an expert" is the spine's terminus. No icons, no group
   eyebrows — type-driven. */
// Path-based AR localization for hardcoded chrome labels (nav links/descs come from content/global_ar.js).
function dzIsAr() { return typeof window !== 'undefined' && window.location.pathname.startsWith('/ar/'); }
function dzT(en, ar) { return dzIsAr() ? ar : en; }

function isMobileNavActive(href, currentPath) {
  if (!href) return false;
  if (href === '/') return currentPath === '/' || currentPath === '/index.html';
  const normalized = currentPath.replace(/\/$/, '');
  const target = href.replace(/\/$/, '');
  return normalized === target || normalized.startsWith(target + '/');
}

/* A single entry inside a dropdown submenu. If the entry itself has
   children (e.g. Solutions → TwinMs → use cases) it renders a nested
   fly-out panel that opens to the side on hover/focus; otherwise it's a
   plain link. The parent entry still navigates (TwinMs → /twinms/) — the
   fly-out is purely the hover affordance, mirroring how the top-level
   dropdown's parent link behaves. State-driven (not CSS :hover-only) with
   the same 120 ms grace period as the parent, so it never sticks open. */
function NavbarSubItem({ child, currentPath, onLeafClick }) {
  const hasChildren = child.children && child.children.length > 0;
  const matches = (href) => href && (currentPath === href || currentPath === href.replace(/\/$/, ''));
  const [subOpen, setSubOpen] = useNavState(false);
  const subTimer = useNavRef(null);

  if (!hasChildren) {
    return (
      <li role="none">
        <a
          href={child.href}
          className="dz-navbar__sublink"
          role="menuitem"
          aria-current={matches(child.href) ? 'page' : undefined}
          onClick={onLeafClick}
        >
          {child.label}
        </a>
      </li>
    );
  }

  const enter = () => {
    if (subTimer.current) { clearTimeout(subTimer.current); subTimer.current = null; }
    setSubOpen(true);
  };
  const leave = () => {
    subTimer.current = setTimeout(() => setSubOpen(false), 120);
  };
  const branchActive = matches(child.href) || (child.children || []).some((g) => matches(g.href));

  return (
    <li
      role="none"
      className={'dz-navbar__subitem' + (subOpen ? ' is-open' : '')}
      onMouseEnter={enter}
      onMouseLeave={leave}
    >
      <a
        href={child.href}
        className="dz-navbar__sublink dz-navbar__sublink--has-children"
        role="menuitem"
        aria-haspopup="true"
        aria-expanded={subOpen}
        aria-current={branchActive ? 'page' : undefined}
        onClick={onLeafClick}
        onFocus={enter}
      >
        <span>{child.label}</span>
        <svg className="dz-navbar__subchevron" width="6" height="10" viewBox="0 0 6 10" aria-hidden="true">
          <path d="M1 1l4 4-4 4" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </a>
      <ul className="dz-navbar__submenu dz-navbar__submenu--nested" role="menu">
        {child.children.map((g) => (
          <li key={g.href} role="none">
            <a
              href={g.href}
              className="dz-navbar__sublink"
              role="menuitem"
              aria-current={matches(g.href) ? 'page' : undefined}
              onClick={onLeafClick}
            >
              {g.label}
            </a>
          </li>
        ))}
      </ul>
    </li>
  );
}

function NavbarDropdown({ link, currentPath }) {
  // Active when the dropdown's own href OR any child href matches the current path
  const isActive = (() => {
    const matches = (href) => href && (currentPath === href || currentPath === href.replace(/\/$/, ''));
    if (matches(link.href)) return true;
    return (link.children || []).some(
      (c) => matches(c.href) || (c.children || []).some((g) => matches(g.href))
    );
  })();
  const [open, setOpen] = useNavState(false);
  const containerRef = useNavRef(null);
  const closeTimerRef = useNavRef(null);

  /* Close on outside click */
  useNavEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  /* Close on Escape */
  useNavEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  /* Cancel any pending close on hover-in (the small grace period below) */
  const handleMouseEnter = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setOpen(true);
  };
  /* Brief delay before closing so the cursor can cross the gap to the submenu */
  const handleMouseLeave = () => {
    closeTimerRef.current = setTimeout(() => setOpen(false), 120);
  };

  return (
    <div
      ref={containerRef}
      className={'dz-navbar__dropdown' + (open ? ' is-open' : '')}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Parent acts as a real link to the section index (e.g. /solutions/).
          Hovering the wrapper opens the submenu; clicking the link navigates.
          The chevron is decorative — purely visual hint that more lives below. */}
      {link.href ? (
        <a
          href={link.href}
          className="dz-navbar__link dz-navbar__link--has-children"
          aria-current={isActive ? 'page' : undefined}
        >
          <span>{link.label}</span>
          <svg
            className="dz-navbar__chevron"
            width="10"
            height="6"
            viewBox="0 0 10 6"
            aria-hidden="true"
          >
            <path
              d="M1 1L5 5L9 1"
              stroke="currentColor"
              strokeWidth="1.6"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </a>
      ) : (
        <button
          type="button"
          className="dz-navbar__link dz-navbar__link--has-children"
          aria-haspopup="true"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          <span>{link.label}</span>
          <svg
            className="dz-navbar__chevron"
            width="10"
            height="6"
            viewBox="0 0 10 6"
            aria-hidden="true"
          >
            <path
              d="M1 1L5 5L9 1"
              stroke="currentColor"
              strokeWidth="1.6"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}

      <ul className="dz-navbar__submenu" role="menu">
        {link.children.map((c) => (
          <NavbarSubItem
            key={c.href}
            child={c}
            currentPath={currentPath}
            onLeafClick={() => setOpen(false)}
          />
        ))}
      </ul>
    </div>
  );
}

/* "The BIM Journey" Services menu — ported from the Shopify theme navbar
   (sections/dz-navbar.liquid, journey layout). A wide panel under the bar:
   kicker + "11 stages · 33 services", a lifecycle line of numbered dots
   that lights the stage under the pointer (or keyboard focus), "All
   services", then every stage with its services and a "Not sure where to
   start?" card. Same open/close behaviour as NavbarDropdown; on a touch
   screen the first tap on Services opens the panel instead of following
   the link. Data: window.DZ_GLOBAL.navbar.journey. Styles: css/nav-journey.css. */
const dzNavPad = (n) => (n < 10 ? '0' : '') + n;
const dzJourneyHref = (j, stage, svc) =>
  j.allHref + stage.slug + '/' + (svc ? '#service-' + svc.slug : '');

function NavbarJourney({ link, journey, currentPath }) {
  const stages = journey.stages || [];
  const serviceCount = stages.reduce((n, s) => n + (s.services || []).length, 0);
  // Same rule as NavbarDropdown, so the Services underline shows on exactly
  // the pages it did before (the link itself and its listed children).
  const isActive = (() => {
    const matches = (href) => href && (currentPath === href || currentPath === href.replace(/\/$/, ''));
    return matches(link.href) || (link.children || []).some((c) => matches(c.href));
  })();
  const [open, setOpen] = useNavState(false);
  const [lit, setLit] = useNavState(0);
  const containerRef = useNavRef(null);
  const closeTimerRef = useNavRef(null);
  const tapRef = useNavRef(null);

  useNavEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      // Esc also closes a panel held open by keyboard focus.
      if (containerRef.current && containerRef.current.contains(document.activeElement)) document.activeElement.blur();
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Touch screens also fire emulated mouseenter/mouseleave around a tap;
  // those are ignored so the tap alone decides (first tap opens, a tap on
  // the open Services link follows it).
  const recentTouch = () => tapRef.current && Date.now() - tapRef.current.at < 1000;
  const handleMouseEnter = () => {
    if (recentTouch()) return;
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setOpen(true);
  };
  const handleMouseLeave = () => {
    if (recentTouch()) return;
    closeTimerRef.current = setTimeout(() => setOpen(false), 120);
  };
  const onPointerDownCapture = (e) => {
    tapRef.current = e.pointerType === 'touch' ? { at: Date.now() } : null;
  };
  const onLinkClick = (e) => {
    if (recentTouch() && !open) { e.preventDefault(); setOpen(true); }
  };
  const close = () => setOpen(false);

  return (
    <div
      ref={containerRef}
      className={'dz-navbar__dropdown dz-navbar__dropdown--journey' + (open ? ' is-open' : '')}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onPointerDownCapture={onPointerDownCapture}
    >
      <a
        href={link.href}
        className="dz-navbar__link dz-navbar__link--has-children"
        aria-current={isActive ? 'page' : undefined}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={onLinkClick}
      >
        <span>{link.label}</span>
        <svg className="dz-navbar__chevron" width="10" height="6" viewBox="0 0 10 6" aria-hidden="true">
          <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </a>

      <div className="dz-navj">
        <div className="dz-navj__head">
          <div>
            <p className="dz-navj__kicker">{journey.kicker}</p>
            <p className="dz-navj__sum">
              <strong>{stages.length}</strong> {journey.stagesWord}{' '}
              <span aria-hidden="true">·</span>{' '}
              <strong>{serviceCount}</strong> {journey.servicesWord}
            </p>
          </div>
          <ol className="dz-navj__line" aria-hidden="true">
            {stages.map((s, i) => (
              <li key={s.slug} className={'dz-navj__dot' + (i === lit ? ' is-on' : '')}><span>{dzNavPad(i + 1)}</span></li>
            ))}
          </ol>
          <a href={journey.allHref} className="dz-navj__all" onClick={close}>
            {journey.allLabel}
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M5 12h13m-5-6 6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </a>
        </div>
        <ol className="dz-navj__grid">
          {stages.map((s, i) => (
            <li
              key={s.slug}
              className="dz-navj__stage"
              style={{ '--i': i }}
              onPointerEnter={() => setLit(i)}
              onFocus={() => setLit(i)}
            >
              <a href={dzJourneyHref(journey, s)} className="dz-navj__stage-name" onClick={close}>
                <span className="dz-navj__num">{dzNavPad(i + 1)}</span>
                <span>{s.label}</span>
              </a>
              <ul className="dz-navj__svcs">
                {(s.services || []).map((v) => (
                  <li key={v.slug}><a href={dzJourneyHref(journey, s, v)} className="dz-navj__svc" onClick={close}>{v.name}</a></li>
                ))}
              </ul>
            </li>
          ))}
          {journey.helpTitle && (
            <li className="dz-navj__help" style={{ '--i': stages.length }}>
              <p className="dz-navj__help-title">{journey.helpTitle}</p>
              <p className="dz-navj__help-text">{journey.helpText}</p>
              <a href={window.DZ_GLOBAL.navbar.ctaHref} className="dz-navj__help-cta" onClick={close}>{window.DZ_GLOBAL.navbar.ctaLabel}</a>
            </li>
          )}
        </ol>
      </div>
    </div>
  );
}

/* Phones: the same journey inside the mobile drawer's Services card — a
   numbered list of stages, each opening to its services, then "All
   services →" (the Shopify theme's mobile panel pattern). */
function NavbarJourneyMobile({ journey, onLeafClick }) {
  return (
    <div className="dz-navj-m">
      <ol className="dz-navj-m__stages">
        {(journey.stages || []).map((s, i) => (
          <li key={s.slug} className="dz-navj-m__stage">
            <details>
              <summary>
                <span className="dz-navj__num">{dzNavPad(i + 1)}</span>
                <span className="dz-navj-m__stage-name">{s.label}</span>
                <svg className="dz-navj-m__plus" width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M7 1v12M1 7h12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
              </summary>
              <ul className="dz-navj-m__svcs">
                {(s.services || []).map((v) => (
                  <li key={v.slug}><a href={dzJourneyHref(journey, s, v)} onClick={onLeafClick}>{v.name}</a></li>
                ))}
              </ul>
            </details>
          </li>
        ))}
      </ol>
      <a href={journey.allHref} className="dz-navj-m__all" onClick={onLeafClick}>{journey.allLabel} <span aria-hidden="true">{dzIsAr() ? '←' : '→'}</span></a>
    </div>
  );
}

/* Language switcher — relocated from the footer into the navbar so it is
   reachable without scrolling: on desktop (in the CTA cluster) and inside the
   mobile drawer (just under the greeting). Reuses the exact routing the old
   footer toggle used — window.DZ_LANG_PAIR_FOR maps the current path to its
   sibling-language URL and localStorage remembers the choice. Hidden until
   body[data-ar-ready="true"] flips (same launch gate the footer used). */
function NavbarLang({ variant }) {
  const cur = dzIsAr() ? 'AR' : 'EN';
  const go = (next) => {
    if (next === cur) return;
    const pairFn = typeof window !== 'undefined' && window.DZ_LANG_PAIR_FOR;
    const dest = pairFn ? pairFn(window.location.pathname, next) : null;
    try { localStorage.setItem('dz-lang', next); } catch (e) {}
    window.location.assign(dest || (next === 'AR' ? '/ar/' : '/'));
  };
  return (
    <div
      className={'dz-navbar__lang' + (variant === 'mobile' ? ' dz-navbar__lang--mobile' : '')}
      role="group"
      aria-label={dzT('Language', 'اللغة')}
    >
      <button
        type="button"
        className={'dz-navbar__lang-btn' + (cur === 'EN' ? ' is-active' : '')}
        aria-current={cur === 'EN' ? 'true' : undefined}
        onClick={() => go('EN')}
      >EN</button>
      <button
        type="button"
        lang="ar"
        className={'dz-navbar__lang-btn' + (cur === 'AR' ? ' is-active' : '')}
        aria-current={cur === 'AR' ? 'true' : undefined}
        onClick={() => go('AR')}
      >العربية</button>
    </div>
  );
}

function Navbar() {
  const data = window.DZ_GLOBAL.navbar;
  const brandName = window.DZ_GLOBAL.brand.name;
  /* Current page path — used to mark the active node on the mobile menu spine */
  const currentPath = (typeof window !== 'undefined' && window.location && window.location.pathname) || '/';

  /* Mobile-menu state — hamburger toggles a slide-down drawer that shows
     all nav links on phones/tablets. Closes on outside click, Escape key,
     and whenever a link is followed (so route changes feel clean). */
  const [mobileOpen, setMobileOpen] = useNavState(false);
  /* Solutions card (Concierge layout) — separate state so its expand/collapse
     is independent of the menu's open/close. Resets when the menu closes. */
  const [solutionsOpen, setSolutionsOpen] = useNavState(false);
  const [servicesOpen, setServicesOpen]   = useNavState(false);
  const [twinmsOpen, setTwinmsOpen]       = useNavState(false);
  useNavEffect(() => {
    if (!mobileOpen) {
      setSolutionsOpen(false);
      setServicesOpen(false);
      setTwinmsOpen(false);
    }
  }, [mobileOpen]);

  useNavEffect(() => {
    if (!mobileOpen) {
      document.body.classList.remove('dz-nav-mobile-open');
      return;
    }
    document.body.classList.add('dz-nav-mobile-open');
    const onKey = (e) => { if (e.key === 'Escape') setMobileOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  const closeMobile = () => setMobileOpen(false);

  return (
    <>
    {/* Skip link — first focusable element on every page. Visually hidden
        until focused, then slides into view in the top-left corner.
        Targets #dz-main on whichever page is rendered. WCAG 2.4.1. */}
    <a href="#dz-main" className="dz-skip-link">{dzT('Skip to main content', 'تخطَّ إلى المحتوى الرئيسي')}</a>
    <header className={'dz-navbar' + (mobileOpen ? ' is-mobile-open' : '')}>
      <a href={window.location.pathname.startsWith('/ar/') ? '/ar/' : '/'} className="dz-navbar__logo" aria-label={brandName + ' home'}>
        <img src={data.logo} alt={data.logoAlt} className="dz-navbar__logo-img" width="1750" height="461" />
      </a>

      <nav className="dz-navbar__nav" aria-label="Primary">
        {data.links.map((l) => {
          if (l.journey && data.journey) {
            return <NavbarJourney key={l.label} link={l} journey={data.journey} currentPath={currentPath} />;
          }
          if (l.children && l.children.length > 0) {
            return <NavbarDropdown key={l.label} link={l} currentPath={currentPath} />;
          }
          const isActive = l.href && (currentPath === l.href || currentPath === l.href.replace(/\/$/, ''));
          return (
            <a
              key={l.href || l.label}
              href={l.href}
              className="dz-navbar__link"
              aria-current={isActive ? 'page' : undefined}
            >
              {l.label}
            </a>
          );
        })}
      </nav>

      <div className="dz-navbar__cta">
        <a href={(window.location.pathname.startsWith('/ar/') ? '/ar' : '') + '/iso-19650/'} className="dz-navbar__iso dz-caption" aria-label={`${data.isoCaption} — open the ISO 19650 page`}>{data.isoCaption}</a>
        <NavbarLang variant="desktop" />
        <a href={data.ctaHref} className="dz-btn dz-btn--aqua dz-btn--sm">
          {data.ctaLabel}
        </a>
      </div>

      {/* Hamburger — visible only on mobile via CSS; clicking it toggles the
          slide-down drawer below (.dz-navbar__mobile). Three lines morph into
          an X when open. ARIA-controlled for screen-reader users. */}
      <button
        type="button"
        className="dz-navbar__hamburger"
        aria-label={mobileOpen ? 'Close navigation menu' : 'Open navigation menu'}
        aria-expanded={mobileOpen}
        aria-controls="dz-navbar-mobile"
        onClick={() => setMobileOpen((o) => !o)}
      >
        <span className="dz-navbar__hamburger-line" />
        <span className="dz-navbar__hamburger-line" />
        <span className="dz-navbar__hamburger-line" />
      </button>
    </header>

        {/* Mobile menu — "Roadmap" design G. Each page is a node on a
        vertical spine; the active page (from window.location) pulses;
        Solutions opens a sub-rail with smaller hollow child nodes; the
        terminus of the spine IS the CTA "Talk to an expert". Rendered as
        a sibling of <header> (NOT a child) so it escapes the navbar's
        backdrop-filter containing block. The open state is driven by
        body.dz-nav-mobile-open (already managed by the useEffect above). */}
    {/* Backdrop — dim+blur overlay behind the panel; tap closes menu.
        z-index 99 (below navbar 100) so navbar stays readable on top. */}
    <div
      className="dz-navbar__mobile-backdrop"
      aria-hidden="true"
      onClick={closeMobile}
    />
    <div
      id="dz-navbar-mobile"
      className="dz-navbar__mobile"
      aria-hidden={!mobileOpen}
      role="dialog"
      aria-modal="true"
      aria-label="Site navigation"
    >
      {(() => {
        // Pull the four L-flavoured sections out of data.links by label.
        // Featured pair = Services + Solutions; Secondary list = the rest
        // (Ai, VDC, Projects, Vision 2030, About) in their declared order.
        // Match by href, not the English label, so the featured accordion
        // (and its nested children) render identically on the Arabic menu,
        // where the labels are localized ('الخدمات' / 'الحلول').
        const isServices  = (l) => /(^|\/)services\/$/.test(l.href || '');
        const isSolutions = (l) => /(^|\/)solutions\/$/.test(l.href || '');
        const services  = data.links.find(isServices);
        const solutions = data.links.find(isSolutions);
        const secondary = data.links.filter((l) => !isServices(l) && !isSolutions(l));
        return (
          <React.Fragment>
            <section className="dz-navbar__mobile-greeting">
              <div className="dz-navbar__mobile-greeting-eyebrow">{data.mobileGreetingEyebrow || 'At your service'}</div>
              {/* greeting is decorative, not the page heading — kept as <p> so each page has exactly one <h1> (the hero). Margin pinned in CSS to the former h1 default (0.67em) so layout is identical. */}
              <p
                className="dz-navbar__mobile-greeting-line"
                dangerouslySetInnerHTML={{ __html: data.mobileGreetingHtml || 'Welcome.<br/>Where would <em>you</em><br/>like to begin?' }}
              />
            </section>

            <NavbarLang variant="mobile" />

            <section className="dz-navbar__mobile-featured" aria-label="Primary destinations">
              {services && (
                // 2026-05-11: Services on mobile now matches the Solutions
                // pattern — it's a <button> that expands a children list
                // (Arch BIM, MEP BIM, Clash detection, Scan to BIM, HBIM,
                // ISO 19650). If a project ever defines Services without
                // children we fall back to the old direct-link <a>.
                services.children && services.children.length > 0 ? (
                  <button
                    type="button"
                    className="dz-navbar__mobile-feat dz-navbar__mobile-feat--primary"
                    data-open={servicesOpen ? 'true' : 'false'}
                    aria-expanded={servicesOpen}
                    aria-controls="dz-mmenu-services-children"
                    onClick={() => setServicesOpen((o) => !o)}
                  >
                    <div className="dz-navbar__mobile-feat-name">{services.label}</div>
                    <div className="dz-navbar__mobile-feat-desc">{services.mobileDesc || 'BIM services across the full project lifecycle.'}</div>
                    <svg className="dz-navbar__mobile-feat-chev" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="1.5"
                         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M9 6l6 6-6 6"/>
                    </svg>
                  </button>
                ) : (
                  <a
                    href={services.href}
                    className="dz-navbar__mobile-feat dz-navbar__mobile-feat--primary"
                    onClick={closeMobile}
                  >
                    <div className="dz-navbar__mobile-feat-name">{services.label}</div>
                    <div className="dz-navbar__mobile-feat-desc">{services.mobileDesc || 'BIM services across the full project lifecycle.'}</div>
                    <svg className="dz-navbar__mobile-feat-chev" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="1.5"
                         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M9 6l6 6-6 6"/>
                    </svg>
                  </a>
                )
              )}
              {services && services.children && services.children.length > 0 && (
                <div
                  className="dz-navbar__mobile-feat-children"
                  id="dz-mmenu-services-children"
                  data-open={servicesOpen ? 'true' : 'false'}
                >
                  <div className="dz-navbar__mobile-feat-children-inner">
                    {services.journey && data.journey ? (
                      <NavbarJourneyMobile journey={data.journey} onLeafClick={closeMobile} />
                    ) : (
                    <ul className="dz-navbar__mobile-feat-children-list">
                      {services.children.map((c) => (
                        <li key={c.href}>
                          <a
                            href={c.href}
                            className={
                              'dz-navbar__mobile-feat-child' +
                              (c.isOverview ? ' dz-navbar__mobile-feat-child--overview' : '')
                            }
                            onClick={closeMobile}
                          >
                            {c.isOverview ? <>{c.label} <span aria-hidden="true">→</span></> : c.label}
                          </a>
                        </li>
                      ))}
                    </ul>
                    )}
                  </div>
                </div>
              )}
              {solutions && (
                <button
                  type="button"
                  className="dz-navbar__mobile-feat dz-navbar__mobile-feat--solutions"
                  data-open={solutionsOpen ? 'true' : 'false'}
                  aria-expanded={solutionsOpen}
                  aria-controls="dz-mmenu-solutions-children"
                  onClick={() => setSolutionsOpen((o) => !o)}
                >
                  <div className="dz-navbar__mobile-feat-name">{solutions.label}</div>
                  <div className="dz-navbar__mobile-feat-desc">{solutions.mobileDesc || 'Two proprietary platforms.'}</div>
                  <svg className="dz-navbar__mobile-feat-chev" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" strokeWidth="1.5"
                       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M9 6l6 6-6 6"/>
                  </svg>
                </button>
              )}
              {solutions && solutions.children && (
                <div
                  className="dz-navbar__mobile-feat-children"
                  id="dz-mmenu-solutions-children"
                  data-open={solutionsOpen ? 'true' : 'false'}
                >
                  <div className="dz-navbar__mobile-feat-children-inner">
                    <ul className="dz-navbar__mobile-feat-children-list">
                      {solutions.href && (
                        <li>
                          <a
                            href={solutions.href}
                            className="dz-navbar__mobile-feat-child dz-navbar__mobile-feat-child--overview"
                            onClick={closeMobile}
                          >
                            {solutions.overviewLabel || 'All solutions'} <span aria-hidden="true">→</span>
                          </a>
                        </li>
                      )}
                      {solutions.children.map((c) => (
                        c.children && c.children.length > 0 ? (
                          // TwinMs — itself expandable into its use cases.
                          // Mirrors the Services/Solutions accordion: the row
                          // is a toggle button, and an overview link to the
                          // product page sits first inside the revealed list.
                          <li key={c.href} className="dz-navbar__mobile-feat-subgroup">
                            <button
                              type="button"
                              className="dz-navbar__mobile-feat-child dz-navbar__mobile-feat-child--expand"
                              data-open={twinmsOpen ? 'true' : 'false'}
                              aria-expanded={twinmsOpen}
                              aria-controls="dz-mmenu-twinms-children"
                              onClick={() => setTwinmsOpen((o) => !o)}
                            >
                              <span>{c.label}</span>
                              <svg className="dz-navbar__mobile-feat-subchev" viewBox="0 0 24 24" fill="none"
                                   stroke="currentColor" strokeWidth="1.5"
                                   strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M9 6l6 6-6 6"/>
                              </svg>
                            </button>
                            <div
                              className="dz-navbar__mobile-feat-subchildren"
                              id="dz-mmenu-twinms-children"
                              data-open={twinmsOpen ? 'true' : 'false'}
                            >
                              <div className="dz-navbar__mobile-feat-subchildren-inner">
                                <ul className="dz-navbar__mobile-feat-subchildren-list">
                                  {c.href && (
                                    <li>
                                      <a
                                        href={c.href}
                                        className="dz-navbar__mobile-feat-child dz-navbar__mobile-feat-child--overview"
                                        onClick={closeMobile}
                                      >
                                        {c.label} <span aria-hidden="true">→</span>
                                      </a>
                                    </li>
                                  )}
                                  {c.children.map((g) => (
                                    <li key={g.href}>
                                      <a
                                        href={g.href}
                                        className="dz-navbar__mobile-feat-child"
                                        onClick={closeMobile}
                                      >
                                        {g.label}
                                      </a>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </li>
                        ) : (
                          <li key={c.href}>
                            <a
                              href={c.href}
                              className="dz-navbar__mobile-feat-child"
                              onClick={closeMobile}
                            >
                              {c.label}
                            </a>
                          </li>
                        )
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </section>

            <ul className="dz-navbar__mobile-secondary" aria-label="More">
              {secondary.map((l) => (
                <li key={l.href || l.label}>
                  <a
                    href={l.href}
                    className="dz-navbar__mobile-secondary-row"
                    onClick={closeMobile}
                  >
                    <span>{l.label}</span>
                    <svg className="dz-navbar__mobile-secondary-arrow" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="1.5"
                         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M5 12h14M13 6l6 6-6 6"/>
                    </svg>
                  </a>
                  {(l.children || []).filter((c) => !c.isOverview).map((c) => (
                    <a
                      key={c.href}
                      href={c.href}
                      className="dz-navbar__mobile-secondary-row"
                      style={{ marginInlineStart: '20px', fontSize: '15px' }}
                      onClick={closeMobile}
                    >
                      <span>{c.label}</span>
                      <svg className="dz-navbar__mobile-secondary-arrow" viewBox="0 0 24 24" fill="none"
                           stroke="currentColor" strokeWidth="1.5"
                           strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M5 12h14M13 6l6 6-6 6"/>
                      </svg>
                    </a>
                  ))}
                </li>
              ))}
            </ul>

            <section className="dz-navbar__mobile-cta-section">
              <p className="dz-navbar__mobile-cta-lead">{dzT('Prefer to speak with a senior engineer?', 'هل تفضّل التحدّث إلى مهندس أول؟')}</p>
              <a
                href={data.ctaHref}
                className="dz-navbar__mobile-cta-link"
                onClick={closeMobile}
              >
                {dzT('Talk to an expert', 'تحدّث إلى خبير')}
                <span className="dz-navbar__mobile-cta-arrow" aria-hidden="true">→</span>
              </a>
            </section>

            <div className="dz-navbar__mobile-foot">
              <span>ISO&nbsp;19650</span>
            </div>
          </React.Fragment>
        );
      })()}
    </div>
    </>
  );
}

window.Navbar = Navbar;
